/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import clsx from 'clsx';
import { motion } from 'motion/react';
import * as React from 'react';
import { mergeProps, useFocusRing, usePress } from 'react-aria';

import { Icon } from '@gitbook/spine-icons';
import { useEventCallback, useResizeObserver } from '@gitbook/util-react-hooks';
import { assert } from '@gitbook/util-ts';

/**
 * Stepper component that displays progress through a sequence of numbered steps.
 */
export function Stepper<StepId extends string>(props: {
    children: React.ReactNode;
    value: StepId;
    onChange: (value: StepId) => void;
    disabled?: boolean;
    className?: string;
    orientation?: React.AriaAttributes['aria-orientation'];
    /**
     * Whether steps are uniform size or not.
     *
     * If set as true, each step is consistently sized with the label and step indicator centered in the step. This can leave whitespace either side.
     *
     * If false, each step is sized by its own content. Where labels are of different lengths this can lead to inconsistent size of steps.
     * The stepper will use all of the width of its parent.
     *
     * @default false
     */
    uniformSteps?: boolean;
}) {
    const { orientation = 'horizontal', uniformSteps = false } = props;
    const steps = React.Children.toArray(props.children).filter(
        (child): child is React.ReactElement<StepperStepProps> => {
            if (!React.isValidElement(child)) {
                return false;
            }

            if (child.type !== StepperStep) {
                throw new Error('Stepper only accepts StepperStep children');
            }

            return true;
        }
    );
    const stepCount = steps.length;
    const bulletRefs = React.useMemo(
        () => Array.from({ length: stepCount }).map(() => React.createRef<HTMLDivElement>()),
        [stepCount]
    );
    const [stepPosition, setStepPosition] = React.useState<Array<number>>([]);
    const activeStepIndex = steps.findIndex((step) => step.props.id === props.value);
    const containerRef = useResizeObserver(() => {
        setStepPosition(
            bulletRefs.map((ref) => {
                assert(ref.current);
                return orientation === 'vertical'
                    ? ref.current.offsetTop + ref.current.offsetHeight / 2
                    : ref.current.offsetLeft + ref.current.offsetWidth / 2;
            })
        );
    });

    // Recompute stepX when bullets change, so that lines are up to date as well.
    // This is necessary for steppers with variable number of steps. E.g. steps
    // affecting the visibility of following steps, like in the end-of-trial flow.
    React.useEffect(() => {
        setStepPosition(
            bulletRefs.map((ref) => {
                if (ref.current) {
                    return orientation === 'vertical'
                        ? ref.current.offsetTop + ref.current.offsetHeight / 2
                        : ref.current.offsetLeft + ref.current.offsetWidth / 2;
                }
                return 0;
            })
        );
    }, [bulletRefs, orientation]);

    const lineStyle = React.useMemo(() => {
        const first = stepPosition[0];
        const last = stepPosition[stepPosition.length - 1];
        if (first === undefined || last === undefined) {
            return null;
        }
        return { start: first, size: last - first };
    }, [stepPosition]);

    const activeLineStyle = React.useMemo(() => {
        const first = stepPosition[0];
        const last = stepPosition[activeStepIndex];
        if (first === undefined || last === undefined) {
            return null;
        }
        return { start: first, size: last - first };
    }, [stepPosition, activeStepIndex]);

    if (steps.length === 0) {
        return null;
    }

    return (
        <div
            ref={containerRef}
            data-orientation={orientation}
            className={clsx(
                'group/stepper relative z-10 grid w-full grid-flow-col justify-between gap-6',
                'data-[orientation=vertical]:grid-flow-row data-[orientation=vertical]:[grid-auto-rows:1fr]',
                uniformSteps ? '[grid-auto-columns:minmax(0,1fr)]' : '[grid-auto-columns:auto]',
                props.className
            )}
        >
            {steps.map((step, index) => {
                return (
                    <StepContainer
                        key={step.props.id}
                        index={index}
                        active={index === activeStepIndex}
                        completed={index < activeStepIndex}
                        onChange={props.onChange}
                        bulletRef={bulletRefs[index]}
                        disabled={props.disabled ?? false}
                    >
                        {step}
                    </StepContainer>
                );
            })}
            {lineStyle && (
                <div
                    className="absolute -z-[1] bg-secondary group-data-[orientation=horizontal]/stepper:top-[calc(0.75rem-1px)] group-data-[orientation=horizontal]/stepper:h-[2px] group-data-[orientation=vertical]/stepper:left-[calc(0.75rem-1px)] group-data-[orientation=vertical]/stepper:w-[2px]"
                    style={
                        orientation === 'vertical'
                            ? {
                                  top: lineStyle.start,
                                  height: lineStyle.size,
                              }
                            : {
                                  left: lineStyle.start,
                                  width: lineStyle.size,
                              }
                    }
                />
            )}
            {activeLineStyle && (
                <motion.div
                    initial={false}
                    animate={
                        orientation === 'vertical'
                            ? { height: activeLineStyle.size }
                            : { width: activeLineStyle.size }
                    }
                    className="absolute -z-[1] bg-action-primary group-data-[orientation=horizontal]/stepper:top-[calc(0.75rem-1px)] group-data-[orientation=horizontal]/stepper:h-[2px] group-data-[orientation=vertical]/stepper:left-[calc(0.75rem-1px)] group-data-[orientation=vertical]/stepper:w-[2px]"
                    style={
                        orientation === 'vertical'
                            ? { top: activeLineStyle.start }
                            : { left: activeLineStyle.start }
                    }
                    transition={{ duration: 0.2 }}
                />
            )}
        </div>
    );
}

function StepContainer(props: {
    index: number;
    active: boolean;
    completed: boolean;
    children: React.ReactNode;
    onChange: (value: string) => void;
    bulletRef: React.RefObject<HTMLDivElement | null>;
    disabled: boolean;
}) {
    const onChange = useEventCallback(props.onChange);
    const value = React.useMemo<StepperStepContextType>(
        () => ({
            active: props.active,
            completed: props.completed,
            index: props.index,
            onChange,
            bulletRef: props.bulletRef,
            disabled: props.disabled,
        }),
        [onChange, props.active, props.completed, props.index, props.bulletRef, props.disabled]
    );
    return (
        <StepperStepContext.Provider value={value}>{props.children}</StepperStepContext.Provider>
    );
}

export type StepperStepContextType = {
    index: number;
    active: boolean;
    completed: boolean;
    onChange: (value: string) => void;
    bulletRef: React.RefObject<HTMLDivElement | null>;
    disabled: boolean;
};

const StepperStepContext = React.createContext<StepperStepContextType | null>(null);

export type StepperStepProps = { id: string; title: React.ReactNode; hasError?: boolean };

export function StepperStep(props: StepperStepProps) {
    const { title, hasError = false } = props;
    const ctx = React.useContext(StepperStepContext);
    assert(ctx, 'StepperStep must be wrapped in a Stepper component');
    const { index, active, completed, bulletRef, disabled } = ctx;
    const pressable = completed && !disabled;
    const { pressProps } = usePress({
        onPress: () => {
            ctx.onChange(props.id);
        },
        isDisabled: !pressable,
    });
    const { focusProps, isFocusVisible } = useFocusRing();
    return (
        <div
            className={clsx(
                'group flex flex-col items-center gap-1 rounded-xs',
                'group-data-[orientation=vertical]/stepper:flex-row group-data-[orientation=vertical]/stepper:gap-4',
                pressable && 'cursor-pointer',
                isFocusVisible && pressable && 'focus-ring'
            )}
            {...(pressable && {
                role: 'button',
                tabIndex: 0,
                ...mergeProps(pressProps, focusProps),
            })}
        >
            <div
                ref={bulletRef}
                className={clsx(
                    'flex size-6 shrink-0 items-center justify-center rounded-full border text-ui-small tabular-nums transition',
                    hasError
                        ? [
                              'border-danger-subtle bg-danger-ui text-danger-low',
                              pressable && 'group-hover:bg-danger-ui-hover',
                          ]
                        : active
                          ? 'border-transparent bg-neutral-solid text-inverted delay-100'
                          : completed
                            ? [
                                  'border-transparent bg-neutral-solid text-inverted',
                                  pressable &&
                                      'group-hover:border-neutral-subtle group-hover:bg-neutral-ui-hover group-hover:text-muted',
                              ]
                            : 'border-neutral-subtle bg-neutral-ui text-muted'
                )}
            >
                {!hasError && completed ? <Icon.Check className="text-[1.2em]" /> : index + 1}
            </div>
            <div
                className={clsx(
                    'text-center text-ui-small',
                    active
                        ? 'text-base'
                        : completed
                          ? clsx('text-muted', pressable && 'group-hover:text-base')
                          : 'text-muted'
                )}
            >
                {title}
            </div>
        </div>
    );
}
